/* @jsx h */
import { defineComponent, h, PropType, ref, watch, onBeforeUnmount } from "vue";
import { RightSheet } from "@lemmonhq/uikit";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type {
  PrintTemplate,
  PrintTemplatePreviewResult,
} from "@lemmonhq/types";
import type { PrintTemplateEditorMode, PrintTemplateFormState } from "./types";
import {
  createDefaultPrintTemplateForm,
  getPrintTemplateTypeLabel,
  printTemplateToFormState,
  PRINT_TEMPLATE_PRESETS,
  PRINT_TEMPLATE_PREVIEW_DEBOUNCE_MS,
  validatePrintTemplateForm,
} from "./utils";
import { PrintTemplatePreviewPane } from "./PrintTemplatePreviewPane";
import { PrintTemplateHelpGuide } from "./PrintTemplateHelpGuide";

// todo: use https://discuss.codemirror.net/t/using-codemirror-lang-liquid-with-lang-markdown-instead-of-lang-html/7148 later to provide syntax aware editing
export const PrintTemplateEditorSheet = defineComponent({
  name: "PrintTemplateEditorSheet",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String as PropType<PrintTemplateEditorMode>,
      required: true,
    },
    template: {
      type: Object as PropType<PrintTemplate | null>,
      default: null,
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: true,
    },
    onSubmit: {
      type: Function as PropType<
        (data: PrintTemplateFormState) => Promise<void>
      >,
      required: true,
    },
    onDelete: {
      type: Function as PropType<(() => Promise<void>) | undefined>,
      default: undefined,
    },
  },
  setup(props) {
    const trpc = useTrpc();
    const toast = useToast();

    const submitting = ref(false);
    const previewLoading = ref(false);
    const previewError = ref<string | null>(null);
    const previewSvg = ref("");
    const previewRequestId = ref(0);
    const form = ref<PrintTemplateFormState>(createDefaultPrintTemplateForm());
    let previewTimeout: ReturnType<typeof setTimeout> | null = null;

    const resetForm = () => {
      form.value =
        props.mode === "edit" && props.template
          ? printTemplateToFormState(props.template)
          : createDefaultPrintTemplateForm();
    };

    const runPreview = async () => {
      const requestId = ++previewRequestId.value;
      previewLoading.value = true;
      previewError.value = null;

      try {
        const result = (await trpc.printTemplates.preview.query({
          type: form.value.type,
          name: form.value.name,
          template: form.value.template,
        })) as PrintTemplatePreviewResult;

        if (requestId !== previewRequestId.value) return;
        previewSvg.value = result.svg;
      } catch (error) {
        if (requestId !== previewRequestId.value) return;
        previewError.value =
          error instanceof Error
            ? error.message
            : "Failed to render template preview.";
      } finally {
        if (requestId === previewRequestId.value) {
          previewLoading.value = false;
        }
      }
    };

    const schedulePreview = () => {
      if (previewTimeout) {
        clearTimeout(previewTimeout);
      }

      previewTimeout = setTimeout(() => {
        runPreview();
      }, PRINT_TEMPLATE_PREVIEW_DEBOUNCE_MS);
    };

    watch(
      () => [props.isOpen, props.mode, props.template] as const,
      ([isOpen]) => {
        if (!isOpen) return;
        resetForm();
        schedulePreview();
      },
      { immediate: true },
    );

    watch(
      () => [form.value.name, form.value.type, form.value.template] as const,
      () => {
        if (!props.isOpen) return;
        schedulePreview();
      },
    );

    onBeforeUnmount(() => {
      if (previewTimeout) {
        clearTimeout(previewTimeout);
      }
    });

    const handleSubmit = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;

      const error = validatePrintTemplateForm(form.value);
      if (error) {
        toast.showToast(error, { type: "warning" });
        return;
      }

      try {
        submitting.value = true;
        await props.onSubmit({
          name: form.value.name.trim(),
          type: form.value.type,
          template: form.value.template,
        });
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!props.onDelete || submitting.value) return;

      try {
        submitting.value = true;
        await props.onDelete();
      } finally {
        submitting.value = false;
      }
    };

    const title =
      props.mode === "create" ? "New print template" : "Edit print template";

    return () => (
      <RightSheet
        isOpen={props.isOpen}
        title={title}
        onClose={props.onClose}
        width="1120px"
      >
        <form class="flex h-full min-h-0 flex-col" onSubmit={handleSubmit}>
          <div class="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_500px]">
            <div class="flex min-h-0 flex-col">
              <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <div class="space-y-1">
                  <p class="text-sm text-base-content/70">
                    Configure editable templates for kitchen tickets and tax
                    receipts. Kitchen tickets use Liquid syntax and tax receipts
                    keep their existing Mustache syntax.
                  </p>
                </div>

                <div class="grid gap-4 md:grid-cols-2">
                  <label class="form-control w-full">
                    <span class="label-text text-sm font-medium text-accent">
                      Name
                    </span>
                    <input
                      class="input input-bordered"
                      placeholder="Friday dinner receipt"
                      value={form.value.name}
                      onInput={(event) => {
                        form.value.name = (
                          event.target as HTMLInputElement
                        ).value;
                      }}
                    />
                  </label>

                  <label class="form-control w-full">
                    <span class="label-text text-sm font-medium text-accent">
                      Type
                    </span>
                    <select
                      class="select select-bordered"
                      value={form.value.type}
                      onChange={(event) => {
                        form.value.type = (event.target as HTMLSelectElement)
                          .value as PrintTemplateFormState["type"];
                      }}
                    >
                      <option value="kitchen_ticket">
                        {getPrintTemplateTypeLabel("kitchen_ticket")}
                      </option>
                      <option value="tax_receipt">
                        {getPrintTemplateTypeLabel("tax_receipt")}
                      </option>
                    </select>
                  </label>
                </div>

                <label class="form-control w-full">
                  <span class="label-text mb-4 text-sm font-medium text-accent">
                    Template
                  </span>
                  <div class="mb-4 grid gap-2 md:grid-cols-2">
                    {PRINT_TEMPLATE_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        class="rounded-2xl border  bg-base-100 px-4 py-3 text-left"
                        onClick={() => {
                          form.value.template = preset.template;
                        }}
                      >
                        <div class="text-sm font-semibold text-base-content">
                          {preset.name}
                        </div>
                        <div class="mt-1 text-xs text-base-content/60">
                          {preset.description}
                        </div>
                      </button>
                    ))}
                  </div>
                  <textarea
                    class="textarea textarea-bordered min-h-[360px] font-mono text-sm leading-6"
                    value={form.value.template}
                    onInput={(event) => {
                      form.value.template = (
                        event.target as HTMLTextAreaElement
                      ).value;
                    }}
                  />
                  <PrintTemplateHelpGuide currentType={form.value.type} />
                </label>
              </div>

              <div class="flex items-center justify-between gap-4 border-t  bg-base-200/40 px-6 py-4">
                <div class="text-sm text-base-content/60">
                  {props.mode === "edit" ? (
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={handleDelete}
                      disabled={submitting.value || !props.onDelete}
                    >
                      Delete template
                    </button>
                  ) : (
                    "Preview updates automatically as you edit. Use the help guide for Liquid variables and filters."
                  )}
                </div>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onClick={props.onClose}
                    disabled={submitting.value}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary btn-sm"
                    disabled={submitting.value}
                  >
                    {submitting.value
                      ? props.mode === "create"
                        ? "Creating..."
                        : "Saving..."
                      : props.mode === "create"
                        ? "Create template"
                        : "Save changes"}
                  </button>
                </div>
              </div>
            </div>

            <PrintTemplatePreviewPane
              svg={previewSvg.value}
              isLoading={previewLoading.value}
              error={previewError.value}
            />
          </div>
        </form>
      </RightSheet>
    );
  },
});
